import React from 'react';
import { motion } from 'framer-motion';
import { Edit, Trash2, Menu as MenuIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ReorderControls, CardLoadingOverlay } from '@/components/ui/ReorderControls';
import type { MenuSection } from '@/types';

interface SectionCardProps {
  section: MenuSection;
  itemCount: number;
  onEdit: (section: MenuSection) => void;
  onDelete: (section: MenuSection) => void;
  onManageItems: (section: MenuSection) => void;
  // New props for reordering
  allSections: MenuSection[];
  onReorderSections: (sections: MenuSection[]) => void;
  isReordering?: boolean; // This specific card is being reordered
  isAnyReordering?: boolean; // Any card is being reordered
}

/**
 * SectionCard - Individual menu section display card
 * Shows section info with action buttons for management
 */
export const SectionCard: React.FC<SectionCardProps> = ({
  section,
  itemCount,
  onEdit,
  onDelete,
  onManageItems,
  allSections,
  onReorderSections,
  isReordering = false,
  isAnyReordering = false,
}) => {
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${section.name}"? This will also delete all items in this section. This action cannot be undone.`)) {
      onDelete(section);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow relative"
    >
      {/* Loading Overlay */}
      <CardLoadingOverlay loading={isReordering} />
      
      {/* Section Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 truncate">
            {section.name}
          </h3>
          {section.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {section.description}
            </p>
          )}
        </div>
        
        {/* Section Status */}
        <div className="ml-4 flex-shrink-0">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            section.isVisible 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {section.isVisible ? 'Visible' : 'Hidden'}
          </span>
        </div>
      </div>

      {/* Item Count */}
      <div className="mb-4 sm:mb-6">
        <p className="text-sm text-gray-600">
          {itemCount === 0 ? (
            'No items yet'
          ) : (
            `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`
          )}
        </p>
      </div>

      {/* Action Buttons - Following Restaurant Card Pattern */}
      <div className="space-y-3">
        {/* All action buttons in a single row (like restaurant card) */}
        <div className="flex items-center justify-center space-x-2 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(section)}
            title="Edit Section"
            className="p-2"
          >
            <Edit className="w-4 h-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
            title="Delete Section"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          
          {/* Reorder Controls */}
          <ReorderControls
            item={section}
            items={allSections}
            onReorder={onReorderSections}
            getItemId={(section) => section.id}
            isAnyReordering={isAnyReordering}
          />
        </div>
        
        {/* Primary Action Button (like restaurant card) */}
        <Button
          variant="primary"
          size="sm"
          onClick={() => onManageItems(section)}
          leftIcon={<MenuIcon className="w-4 h-4" />}
          className="w-full"
        >
          Manage Items
        </Button>
      </div>
    </motion.div>
  );
};